import React, { useState } from 'react';
import type { Socket } from 'socket.io-client';

type SignatureElement = 'flame' | 'storm' | 'tide' | 'shadow' | 'iron' | 'spirit';
type WeaponRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

interface AdminWildcardPowerPanelProps {
  socket?: Socket | null;
  playerId?: string;
  isBusy: boolean;
  onStateChange: (state: { isBusy: boolean; lastMessage: string | null }) => void;
}

// Extended color configurations with secondary and tertiary colors
const ELEMENT_COLORS: Record<SignatureElement, { primary: string; secondary: string; tertiary: string; label: string }> = {
  flame: { primary: '#ff6b35', secondary: '#ff4500', tertiary: '#ffa500', label: '🔥 Flame' },
  storm: { primary: '#00bfff', secondary: '#7df9ff', tertiary: '#87ceeb', label: '⚡ Storm' },
  tide: { primary: '#1e90ff', secondary: '#0066cc', tertiary: '#00bfff', label: '💧 Tide' },
  shadow: { primary: '#4b0082', secondary: '#2f0047', tertiary: '#1a0033', label: '🌑 Shadow' },
  iron: { primary: '#c0c0c0', secondary: '#808080', tertiary: '#505050', label: '⚙️ Iron' },
  spirit: { primary: '#9932cc', secondary: '#8b008b', tertiary: '#4b0082', label: '👻 Spirit' },
};

const WEAPON_COLORS: Record<WeaponRarity, { primary: string; label: string }> = {
  common: { primary: '#cccccc', label: '⚪ Common' },
  uncommon: { primary: '#4caf50', label: '🟢 Uncommon' },
  rare: { primary: '#2196f3', label: '🔵 Rare' },
  epic: { primary: '#9c27b0', label: '🟣 Epic' },
  legendary: { primary: '#ffd700', label: '⭐ Legendary' },
};

// Rarity intensity multipliers
const RARITY_INTENSITY_MULTIPLIERS: Record<WeaponRarity, number> = {
  common: 1.0,
  uncommon: 1.2,
  rare: 1.4,
  epic: 1.6,
  legendary: 2.0,
};

const ELEMENT_INTENSITY_BONUS = 1.3;

// Sample signature moves for testing
const SAMPLE_MOVES: Array<{ id: string; name: string; element: SignatureElement }> = [
  { id: 'flame_burst', name: 'Flame Burst', element: 'flame' },
  { id: 'storm_strike', name: 'Storm Strike', element: 'storm' },
  { id: 'tidal_wave', name: 'Tidal Wave', element: 'tide' },
  { id: 'shadow_step', name: 'Shadow Step', element: 'shadow' },
  { id: 'iron_body', name: 'Iron Body', element: 'iron' },
  { id: 'spirit_pressure', name: 'Spirit Pressure', element: 'spirit' },
];

export const AdminWildcardPowerPanel: React.FC<AdminWildcardPowerPanelProps> = ({
  socket,
  playerId,
  isBusy,
  onStateChange,
}) => {
  const [selectedElement, setSelectedElement] = useState<SignatureElement>('flame');
  const [selectedRarity, setSelectedRarity] = useState<WeaponRarity>('legendary');
  const [testPower, setTestPower] = useState<string>('80');
  const [intensityMultiplier, setIntensityMultiplier] = useState<string>('1.0');
  const [aoeRadius, setAoeRadius] = useState<string>('2');
  const [showColorDetails, setShowColorDetails] = useState(false);

  // Calculate combined visual intensity
  const getRarityIntensity = (): number => {
    return RARITY_INTENSITY_MULTIPLIERS[selectedRarity];
  };

  const getCombinedIntensity = (): number => {
    const rarityIntensity = getRarityIntensity();
    return rarityIntensity * ELEMENT_INTENSITY_BONUS;
  };

  const fireTestCannon = (rarity: WeaponRarity, element?: SignatureElement): void => {
    if (!socket) {
      onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
      return;
    }

    const power = parseInt(testPower, 10);
    if (isNaN(power) || power < 1 || power > 100) {
      onStateChange({ isBusy: false, lastMessage: 'Power must be 1-100' });
      return;
    }

    const aoe = parseInt(aoeRadius, 10);
    if (isNaN(aoe) || aoe < 0 || aoe > 10) {
      onStateChange({ isBusy: false, lastMessage: 'AOE must be 0-10' });
      return;
    }

    const intensity = parseFloat(intensityMultiplier);
    if (isNaN(intensity) || intensity < 0.5 || intensity > 3.0) {
      onStateChange({ isBusy: false, lastMessage: 'Intensity must be 0.5-3.0' });
      return;
    }

    onStateChange({ isBusy: true, lastMessage: `Testing ${rarity} cannon${element ? ` (${element})` : ''}...` });

    // Emit test cannon fire event
    socket.emit('admin:testCannonFire', {
      weaponRarity: rarity,
      signatureElement: element || null,
      powerLevel: power,
      aoeRadius: aoe,
      intensityMultiplier: intensity,
    }, (response: { success: boolean; message: string }) => {
      onStateChange({
        isBusy: false,
        lastMessage: response.message,
      });
    });
  };

  const equipSignatureMove = (moveId: string, element: SignatureElement): void => {
    if (!socket || !playerId) {
      onStateChange({ isBusy: false, lastMessage: 'Socket not connected' });
      return;
    }

    onStateChange({ isBusy: true, lastMessage: `Equipping ${moveId}...` });

    socket.emit('admin:equipSignatureMove', {
      playerId,
      moveId,
    }, (response: { success: boolean; message: string }) => {
      onStateChange({
        isBusy: false,
        lastMessage: response.message,
      });
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Visual Intensity Monitor */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>📊 Visual Intensity Monitor</div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: '#a0aec0' }}>
            <span>Weapon Rarity Intensity:</span>
            <span style={{ fontWeight: 'bold', color: WEAPON_COLORS[selectedRarity].primary }}>
              {getRarityIntensity().toFixed(2)}x
            </span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: '#a0aec0' }}>
            <span>With Signature Bonus:</span>
            <span style={{ fontWeight: 'bold', color: ELEMENT_COLORS[selectedElement].primary }}>
              {getCombinedIntensity().toFixed(2)}x
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: '#a0aec0' }}>
            <span>Custom Multiplier:</span>
            <span style={{ fontWeight: 'bold', color: '#ffd700' }}>
              {intensityMultiplier}x
            </span>
          </div>

          {/* Intensity Multiplier Slider */}
          <div style={{ marginTop: 2 }}>
            <div style={{ fontSize: 8, color: '#a0aec0', marginBottom: 1 }}>Adjust Intensity (0.5-3.0):</div>
            <input
              type="range"
              min="0.5"
              max="3.0"
              step="0.1"
              value={intensityMultiplier}
              onChange={(e) => setIntensityMultiplier(e.target.value)}
              disabled={isBusy}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* Element Color Preview */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div
          style={{
            fontWeight: 600,
            marginBottom: 4,
            fontSize: 10,
            cursor: 'pointer',
            userSelect: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          onClick={() => setShowColorDetails(!showColorDetails)}
        >
          🎨 Element Color Preview
          <span style={{ fontSize: 8, color: '#a0aec0' }}>
            {showColorDetails ? '▼' : '▶'}
          </span>
        </div>

        {showColorDetails && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(Object.entries(ELEMENT_COLORS) as Array<[SignatureElement, typeof ELEMENT_COLORS[SignatureElement]]>).map(
              ([element, { primary, secondary, tertiary, label }]) => (
                <div
                  key={element}
                  style={{
                    padding: '4px',
                    border: '1px solid #2d3748',
                    backgroundColor: '#0d1117',
                  }}
                >
                  <div style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 2, color: primary }}>
                    {label}
                  </div>
                  <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: primary,
                        border: '1px solid #4a5568',
                        borderRadius: '2px',
                      }}
                      title="Primary"
                    />
                    <div style={{ fontSize: 8, color: '#a0aec0' }}>{primary}</div>
                    
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: secondary,
                        border: '1px solid #4a5568',
                        borderRadius: '2px',
                      }}
                      title="Secondary"
                    />
                    <div style={{ fontSize: 8, color: '#a0aec0' }}>{secondary}</div>
                    
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: tertiary,
                        border: '1px solid #4a5568',
                        borderRadius: '2px',
                      }}
                      title="Tertiary"
                    />
                    <div style={{ fontSize: 8, color: '#a0aec0' }}>{tertiary}</div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Weapon Rarity Test Section with Tier Visualization */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>⚔️ Weapon Rarity Test</div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 4 }}>
          <div style={{ fontSize: 9, color: '#a0aec0' }}>Weapon Tier (no signature):</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(Object.entries(WEAPON_COLORS) as Array<[WeaponRarity, { primary: string; label: string }]>).map(
              ([rarity, { primary, label }], index) => {
                const rarityIntensity = RARITY_INTENSITY_MULTIPLIERS[rarity];
                const rarityNumber = index + 1;
                
                return (
                  <div key={rarity} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => fireTestCannon(rarity)}
                      style={{
                        padding: '2px 6px',
                        fontSize: 9,
                        backgroundColor: primary,
                        color: rarity === 'legendary' ? '#000' : '#fff',
                        border: 'none',
                        borderRadius: 0,
                        cursor: isBusy ? 'default' : 'pointer',
                        fontWeight: 'bold',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      title={`Test ${rarity} weapon tier - ${rarityIntensity.toFixed(1)}x intensity`}
                    >
                      <span>{label}</span>
                      <span style={{ fontSize: 8, opacity: 0.7 }}>Tier {rarityNumber}</span>
                    </button>
                    
                    {/* Tier visualization bar */}
                    <div style={{
                      display: 'flex',
                      gap: '1px',
                      marginBottom: 1,
                    }}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div
                          key={i}
                          style={{
                            flex: 1,
                            height: '3px',
                            backgroundColor: i < rarityNumber ? primary : '#2d3748',
                            opacity: i < rarityNumber ? 1 : 0.3,
                            borderRadius: '1px',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>

        <div style={{ fontSize: 9, color: '#a0aec0', marginBottom: 2 }}>Power: {testPower}%</div>
        <input
          type="range"
          min="1"
          max="100"
          value={testPower}
          onChange={(e) => setTestPower(e.target.value)}
          disabled={isBusy}
          style={{ width: '100%', marginBottom: 4 }}
        />
      </div>

      {/* AOE Radius Control */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>🎯 AOE Radius Control</div>
        
        <div style={{ fontSize: 9, color: '#a0aec0', marginBottom: 2 }}>
          Radius: {aoeRadius} tiles
        </div>
        <input
          type="range"
          min="0"
          max="10"
          value={aoeRadius}
          onChange={(e) => setAoeRadius(e.target.value)}
          disabled={isBusy}
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 8, color: '#708090', marginTop: 2 }}>
          0 = Point damage, 10 = Maximum radius
        </div>
      </div>

      {/* Signature Element Test Section */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>✨ Signature Move Test</div>
        
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 9, color: '#a0aec0', marginBottom: 2 }}>Element:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(Object.entries(ELEMENT_COLORS) as Array<[SignatureElement, typeof ELEMENT_COLORS[SignatureElement]]>).map(
              ([element, { primary, label }]) => (
                <button
                  key={element}
                  type="button"
                  disabled={isBusy}
                  onClick={() => setSelectedElement(element)}
                  style={{
                    padding: '2px 6px',
                    fontSize: 9,
                    backgroundColor: selectedElement === element ? primary : '#2d3748',
                    color: selectedElement === element ? (element === 'iron' ? '#000' : '#fff') : '#a0aec0',
                    border: selectedElement === element ? `2px solid ${primary}` : '1px solid #4a5568',
                    borderRadius: 0,
                    cursor: isBusy ? 'default' : 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  {label}
                </button>
              )
            )}
          </div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 9, color: '#a0aec0', marginBottom: 2 }}>Weapon Tier:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(Object.entries(WEAPON_COLORS) as Array<[WeaponRarity, { primary: string; label: string }]>).map(
              ([rarity, { primary, label }]) => (
                <button
                  key={rarity}
                  type="button"
                  disabled={isBusy}
                  onClick={() => setSelectedRarity(rarity)}
                  style={{
                    padding: '2px 6px',
                    fontSize: 9,
                    backgroundColor: selectedRarity === rarity ? primary : '#2d3748',
                    color: selectedRarity === rarity ? (rarity === 'legendary' ? '#000' : '#fff') : '#a0aec0',
                    border: selectedRarity === rarity ? `2px solid ${primary}` : '1px solid #4a5568',
                    borderRadius: 0,
                    cursor: isBusy ? 'default' : 'pointer',
                    fontWeight: 'bold',
                  }}
                >
                  {label}
                </button>
              )
            )}
          </div>
        </div>

        <button
          type="button"
          disabled={isBusy}
          onClick={() => fireTestCannon(selectedRarity, selectedElement)}
          style={{
            width: '100%',
            padding: '2px 6px',
            fontSize: 10,
            backgroundColor: ELEMENT_COLORS[selectedElement].primary,
            color: selectedElement === 'iron' ? '#000' : '#fff',
            border: 'none',
            borderRadius: 0,
            cursor: isBusy ? 'default' : 'pointer',
            fontWeight: 'bold',
          }}
        >
          Fire {ELEMENT_COLORS[selectedElement].label} ({WEAPON_COLORS[selectedRarity].label})
        </button>
      </div>

      {/* Quick Equip Section */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>🎯 Quick Equip Moves</div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SAMPLE_MOVES.map((move) => (
            <button
              key={move.id}
              type="button"
              disabled={isBusy || !socket || !playerId}
              onClick={() => equipSignatureMove(move.id, move.element)}
              style={{
                padding: '2px 6px',
                fontSize: 9,
                backgroundColor: ELEMENT_COLORS[move.element].primary,
                color: move.element === 'iron' ? '#000' : '#fff',
                border: 'none',
                borderRadius: 0,
                cursor: isBusy ? 'default' : 'pointer',
                fontWeight: 'bold',
              }}
              title={`Equip ${move.name}`}
            >
              {ELEMENT_COLORS[move.element].label} {move.name}
            </button>
          ))}
        </div>
      </div>

      {/* Enhanced Color Reference */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>📋 Testing Guide</div>
        
        <div style={{ fontSize: 8, color: '#a0aec0', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div>
            <strong>Intensity Monitor:</strong> Shows rarity multiplier + signature bonus calculation
          </div>
          <div>
            <strong>Custom Multiplier:</strong> Override intensity (0.5-3.0x) for stress testing
          </div>
          <div>
            <strong>AOE Radius:</strong> Test different blast radiuses (0-10 tiles)
          </div>
          <div>
            <strong>Element Colors:</strong> 6 elements × 3 colors each = 18 color layers
          </div>
          <div>
            <strong>Total Combinations:</strong> 6 elements × 5 tiers × 3 intensity options = 90+ unique visual variations
          </div>
        </div>
      </div>
    </div>
  );
};
